// 管理画面の 3 一覧（センサー / 組織 / アカウント）で、管理元（環境機器 または 代理店）を
// 表示・並び替え・絞り込みするための部品。3 一覧が同じ判定をするため、画面側ではなくここに置く。
// API の `managing_distributor_id`（代理店ユーザーの id。環境機器は null）を受け取る。
// libs は Vue に依存しない方針のため、表示名は呼び出し側から渡すか、言語別の辞書から引く

// 管理元 Selectbox の選択値。代理店は代理店ユーザーの id（`User-...`）をそのまま使う。
// 未選択（null）と MANAGED_BY_ALL はどちらも全件を表示する
export const MANAGED_BY_ALL = 'all';
export const MANAGED_BY_DIRECT = 'direct';

// 環境機器は会社名のため、列・選択肢・検索で同じ表記を使う
const DIRECT_LABELS = {
  ja: '環境機器',
  en: 'Kankyo Kiki',
};

/**
 * 環境機器の表示名を返す。
 *
 * @param {string} locale
 * @returns {string}
 */
export const directLabel = locale => DIRECT_LABELS[locale] || DIRECT_LABELS.en;

/**
 * ユーザーの中から代理店を取り出し、名前順に並べる。
 *
 * @param {Array<{id: string, name: string, distributor?: boolean}>} users ユーザーの配列
 * @returns {Array<{id: string, name: string}>}
 */
export const extractDistributors = users => (
  users
    .filter(user => user.distributor)
    .sort((a, b) => a.name.localeCompare(b.name))
);

/**
 * 一覧の 1 行に付ける管理元の表示名と並び替えキーを返す。
 *
 * @param {string|null|undefined} managingDistributorId 行の `managing_distributor_id`。null は環境機器
 * @param {Array<{id: string, name: string}>} distributors `extractDistributors` の戻り値
 * @param {string} directName 環境機器の表示名
 * @returns {{managedByName: (string|null), managedByIsDirect: boolean, managedBySortKey: string}}
 *          代理店が見つからないとき managedByName は null（画面は「--」を出す）
 */
export const describeManagedBy = (managingDistributorId, distributors, directName) => {
  // 並び替えは 環境機器 → 代理店名の昇順 → 名前が引けないもの の順。
  // Vuetify の並び替えは数値に見える値を数値で比べるため、先頭は数字ではなく英字にする
  if (managingDistributorId == null) {
    return { managedByName: directName, managedByIsDirect: true, managedBySortKey: 'a' };
  }
  const distributor = distributors.find(d => d.id === managingDistributorId);
  if (!distributor) {
    return { managedByName: null, managedByIsDirect: false, managedBySortKey: 'c' };
  }
  return {
    managedByName: distributor.name,
    managedByIsDirect: false,
    managedBySortKey: `b${distributor.name}`,
  };
};

/**
 * 管理元 Selectbox の選択に行が当てはまるかを返す。
 *
 * @param {string|null|undefined} managingDistributorId 行の `managing_distributor_id`
 * @param {string|null} selected Selectbox の選択値
 * @returns {boolean}
 */
export const matchesManagedBy = (managingDistributorId, selected) => {
  if (selected == null || selected === MANAGED_BY_ALL) return true;
  if (selected === MANAGED_BY_DIRECT) return managingDistributorId == null;
  return managingDistributorId === selected;
};

/**
 * 管理元 Selectbox の項目を作る。代理店は見出しの下に並べ、代理店が無ければ見出しを出さない。
 *
 * @param {Array<{id: string, name: string}>} distributors `extractDistributors` の戻り値
 * @param {{all: string, direct: string, distributors: string}} labels 表示名
 * @returns {Array} v-select の items
 */
export const toManagedBySelectItems = (distributors, labels) => {
  const items = [
    { text: labels.all, value: MANAGED_BY_ALL },
    { text: labels.direct, value: MANAGED_BY_DIRECT },
  ];
  if (distributors.length === 0) return items;

  return [
    ...items,
    { divider: true },
    { header: labels.distributors },
    ...distributors.map(distributor => ({ text: distributor.name, value: distributor.id })),
  ];
};
